import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from "react-router-dom";
import { eventEmitter, EventMap } from "../helpers/eventEmitter";

const EMPTY_ALERTS: Alert[] = [];

export interface Alert {
    id: string;
    message: string;
    autoClose?: boolean;
    duration?: number;
    title: string;
    description?: string;
    hideTitle?: boolean;
    icon?: React.ReactNode;
    primaryAction?: React.ReactNode;
    secondaryAction?: React.ReactNode;
    variant?: 'critical' | 'info' | 'success' | 'upsell' | 'warning';
    // Custom alert view
    customComponent?: React.ComponentType<any>;
    customProps?: Record<string, any>;
    isCustom?: boolean;
}

interface AlertContextProps {
    alert: Alert | null;
    alerts: Alert[];
    setAlert: (alert: Alert | null) => void;
    addAlert: (alert: Omit<Alert, 'id'>) => void;
    removeAlert: (id: string) => void;
    clearAlerts: () => void;
    setError: (text: string, title?: string | undefined) => void;
    setInfo: (text: string) => void;
    setSuccess: (text: string) => void;
    // Custom alert view
    findAlert: (id: string) => Alert | undefined;
    updateAlert: (id: string, updates: Partial<Omit<Alert, 'id'>>) => void;
    addCustomAlert: <T extends Record<string, any>>(
        component: React.ComponentType<T>,
        props: T,
        options?: Partial<Omit<Alert, 'id' | 'customComponent' | 'customProps' | 'isCustom'>>
    ) => string;
}

const AlertContext = createContext<AlertContextProps>({
    alert: null,
    alerts: [],
    setAlert: () => { },
    addAlert: () => { },
    removeAlert: () => { },
    clearAlerts: () => { },
    setError: () => { },
    setInfo: () => { },
    setSuccess: () => { },
    findAlert: () => undefined,
    updateAlert: () => { },
    addCustomAlert: () => '',
});

export const useAlert = () => useContext(AlertContext);

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>(EMPTY_ALERTS);
    const alert = alerts.length > 0 ? alerts[alerts.length - 1] : null;
    const location = useLocation();

    useEffect(() => {
        const handleAlert = (payload: EventMap['alert']) => {
            addAlert(payload);
        };

        eventEmitter.on('alert', handleAlert);
        return () => {
            eventEmitter.off('alert', handleAlert);
        };
    }, []);

    // Очистить алерты при смене страницы
    useEffect(() => {
        clearAlerts();
    }, [location.pathname]);

    const generateId = () => {
        return Date.now().toString() + Math.random().toString(36).substr(2, 9);
    };

    const addAlert = (alertData: Omit<Alert, 'id'>) => {
        const newAlert: Alert = {
            ...alertData,
            id: generateId(),
        };

        setAlerts(prev => [newAlert, ...prev])

        // Auto close
        if (newAlert.autoClose !== false) {
            const duration = newAlert.duration || 5000;
            setTimeout(() => {
                removeAlert(newAlert.id);
            }, duration);
        }
    };

    const removeAlert = (id: string) => {
        setAlerts(prev => {
            const filtered = prev.filter(alert => alert.id !== id);
            return filtered.length === 0 ? EMPTY_ALERTS : filtered;
        });
    };

    const clearAlerts = () => {
        setAlerts(EMPTY_ALERTS);
    };

    // Custom alert view
    const findAlert = (id: string): Alert | undefined => {
        return alerts.find(alert => alert.id === id);
    };

    const updateAlert = (id: string, updates: Partial<Omit<Alert, 'id'>>) => {
        setAlerts(prev => prev.map(alert =>
            alert.id === id
                ? { ...alert, ...updates }
                : alert
        ));
    };

    const addCustomAlert = <T extends Record<string, any>>(
        component: React.ComponentType<T>,
        props: T,
        options: Partial<Omit<Alert, 'id' | 'customComponent' | 'customProps' | 'isCustom'>> = {}
    ): string => {
        const alertData: Omit<Alert, 'id'> = {
            message: '',
            title: 'Custom Alert',
            isCustom: true,
            customComponent: component,
            customProps: props,
            autoClose: false,
            ...options,
        };

        const id = generateId();
        const newAlert: Alert = { ...alertData, id };

        setAlerts(prev => [newAlert, ...prev])

        if (newAlert.autoClose !== false && newAlert.duration) {
            setTimeout(() => {
                removeAlert(id);
            }, newAlert.duration);
        }

        return id;
    };


    const setAlert = (alert: Alert | null) => {
        if (alert) {
            addAlert(alert);
        } else {
            clearAlerts();
        }
    };

    const setError = (text: string, title?: string | undefined) => {
        addAlert({
            variant: "critical",
            title: title || "Error",
            message: text,
            hideTitle: title === undefined,
            autoClose: true,
            duration: 5000
        });
    };

    const setInfo = (text: string) => {
        addAlert({
            variant: "info",
            title: "Info",
            message: text,
            autoClose: true,
            duration: 5000
        });
    };

    const setSuccess = (text: string) => {
        addAlert({
            variant: "success",
            title: "Success",
            message: text,
            autoClose: true,
            duration: 5000
        });
    };

    return (
        <AlertContext.Provider value={{
            alert,
            alerts,
            setAlert,
            addAlert,
            removeAlert,
            clearAlerts,
            findAlert,
            updateAlert,
            addCustomAlert,
            setError,
            setInfo,
            setSuccess
        }}>
            {children}
        </AlertContext.Provider>
    );
};